import { Component } from '@theme/component';

/**
 * @typedef {Object} PriceRangeSliderRefs
 * @property {HTMLInputElement} minRange - The minimum range input
 * @property {HTMLInputElement} maxRange - The maximum range input
 */

/**
 * Dual-thumb slider that mirrors the price facet text inputs.
 *
 * Dragging updates the sibling min/max text inputs. The native `change` fired on release
 * bubbles up to `price-facet-component` (`on:change="/updatePriceFilterAndResults"`), which
 * applies the filter from those text inputs. The range inputs have no `name`, so they are
 * never serialized into the filter URL.
 *
 * @extends {Component<PriceRangeSliderRefs>}
 */
class PriceRangeSlider extends Component {
  requiredRefs = ['minRange', 'maxRange'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('input', this.#onRangeInput);
    this.#textInputs.min?.addEventListener('change', this.#onTextChange);
    this.#textInputs.max?.addEventListener('change', this.#onTextChange);
    this.#updateFill();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('input', this.#onRangeInput);
    this.#textInputs.min?.removeEventListener('change', this.#onTextChange);
    this.#textInputs.max?.removeEventListener('change', this.#onTextChange);
  }

  /**
   * @returns {{ min: HTMLInputElement | null, max: HTMLInputElement | null }}
   */
  get #textInputs() {
    const facet = this.closest('price-facet-component');
    return {
      min: facet?.querySelector('input[ref="minInput"]') ?? null,
      max: facet?.querySelector('input[ref="maxInput"]') ?? null,
    };
  }

  /**
   * Keeps thumbs from crossing and mirrors values into the text inputs.
   * @param {Event} event
   */
  #onRangeInput = (event) => {
    const { minRange, maxRange } = this.refs;
    const low = Number(minRange.value);
    const high = Number(maxRange.value);

    if (event.target === minRange && low > high) minRange.value = String(high);
    if (event.target === maxRange && high < low) maxRange.value = String(low);

    const { min, max } = this.#textInputs;
    const rangeMax = Number(maxRange.max);

    if (min) min.value = Number(minRange.value) > 0 ? minRange.value : '';
    if (max) max.value = Number(maxRange.value) < rangeMax ? maxRange.value : '';

    this.#updateFill();
  };

  /**
   * Moves the thumbs when the shopper types a price instead of dragging.
   */
  #onTextChange = () => {
    const { minRange, maxRange } = this.refs;
    const { min, max } = this.#textInputs;

    minRange.value = String(this.#toNumber(min?.value, 0));
    maxRange.value = String(this.#toNumber(max?.value, Number(maxRange.max)));
    this.#updateFill();
  };

  /**
   * @param {string | undefined} value - Display value such as "1,250.00"
   * @param {number} fallback
   * @returns {number}
   */
  #toNumber(value, fallback) {
    const parsed = parseFloat((value ?? '').replace(/[^\d.]/g, ''));
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  #updateFill() {
    const { minRange, maxRange } = this.refs;
    const rangeMax = Number(maxRange.max) || 1;
    const low = Number(minRange.value) / rangeMax;
    const high = Number(maxRange.value) / rangeMax;

    // Fractions (0–1) consumed by .price-range__track in facets-sidebar.css
    this.style.setProperty('--range-low', String(low));
    this.style.setProperty('--range-high', String(high));
    // Let the min thumb be grabbed when both thumbs sit at the far right
    minRange.style.zIndex = low > 0.9 ? '3' : '1';
  }
}

if (!customElements.get('price-range-component')) {
  customElements.define('price-range-component', PriceRangeSlider);
}
